import React, { useEffect, useState, useCallback } from "react";
import { getHomeGallery } from "../../../api/storefront";
import { getImageUrl } from "../../../api/client";
import "./HomeGallerySection.css";

/** Homepage image mosaic — renders after blog; empty/hint state when Admin has no images yet. */
export default function HomeGallerySection() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getHomeGallery()
      .then((rows) => {
        if (!cancelled) setItems(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const showPrev = useCallback(() =>
    setLightboxIndex((i) => (i - 1 + items.length) % items.length), [items.length]);
  const showNext = useCallback(() =>
    setLightboxIndex((i) => (i + 1) % items.length), [items.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowRight") showNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex, closeLightbox, showPrev, showNext]);

  if (!loaded) {
    return (
      <section
        className="homeGallerySection homeGallerySection--pending"
        aria-busy="true"
        aria-labelledby="home-gallery-heading"
      >
        <div className="homeGallerySectionInner">
          <header className="homeGallerySectionHeader">
            <h2 id="home-gallery-heading">Gallery</h2>
            <p>Moments from our studio and showcases.</p>
          </header>
          <p className="homeGallerySectionStatus">Loading…</p>
        </div>
      </section>
    );
  }

  if (items.length === 0) {
    return (
      <section className="homeGallerySection homeGallerySection--empty" aria-labelledby="home-gallery-heading">
        <div className="homeGallerySectionInner">
          <header className="homeGallerySectionHeader">
            <h2 id="home-gallery-heading">Gallery</h2>
            <p>Moments from our studio and showcases.</p>
          </header>
          <p className="homeGallerySectionEmptyHint">
            Images appear here once added in <strong>Admin → Home gallery</strong>.
          </p>
        </div>
      </section>
    );
  }

  const lightboxItem = lightboxIndex !== null ? items[lightboxIndex] : null;
  const lightboxSrc = lightboxItem ? getImageUrl(lightboxItem.image_url) : null;

  return (
    <>
      <section className="homeGallerySection" aria-labelledby="home-gallery-heading">
        <div className="homeGallerySectionInner">
          <header className="homeGallerySectionHeader">
            <h2 id="home-gallery-heading">Gallery</h2>
            <p>Moments from our studio and showcases.</p>
          </header>
          <ul className="homeGallerySectionGrid">
            {items.map((item, index) => {
              const imgSrc = getImageUrl(item.image_url);
              const alt = item.alt_text || "";
              return (
                <li key={item.id} className="homeGallerySectionItem">
                  <figure
                    className="homeGalleryFigure homeGalleryFigure--clickable"
                    onClick={() => imgSrc && setLightboxIndex(index)}
                    role={imgSrc ? "button" : undefined}
                    tabIndex={imgSrc ? 0 : undefined}
                    aria-label={imgSrc ? `View ${alt || "gallery image"} full size` : undefined}
                    onKeyDown={(e) => e.key === "Enter" && imgSrc && setLightboxIndex(index)}
                  >
                    {imgSrc ? (
                      <img
                        src={imgSrc}
                        alt={alt || "Gallery image"}
                        loading="lazy"
                        decoding="async"
                        className="homeGalleryImg"
                      />
                    ) : (
                      <div className="homeGalleryImg homeGalleryImg--missing" />
                    )}
                    {imgSrc && <span className="homeGalleryZoomIcon" aria-hidden="true">&#x26F6;</span>}
                    {item.caption ? <figcaption className="homeGalleryCaption">{item.caption}</figcaption> : null}
                  </figure>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {lightboxIndex !== null && (
        <div
          className="galleryLightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          onClick={closeLightbox}
        >
          <button
            className="galleryLightboxClose"
            onClick={closeLightbox}
            aria-label="Close preview"
          >
            &#x2715;
          </button>

          {items.length > 1 && (
            <button
              className="galleryLightboxNav galleryLightboxNav--prev"
              onClick={(e) => { e.stopPropagation(); showPrev(); }}
              aria-label="Previous image"
            >
              &#8249;
            </button>
          )}

          <div className="galleryLightboxContent" onClick={(e) => e.stopPropagation()}>
            {lightboxSrc ? (
              <img
                src={lightboxSrc}
                alt={lightboxItem?.alt_text || "Gallery image"}
                className="galleryLightboxImg"
              />
            ) : null}
            {lightboxItem?.caption && (
              <p className="galleryLightboxCaption">{lightboxItem.caption}</p>
            )}
            {items.length > 1 && (
              <p className="galleryLightboxCounter">{lightboxIndex + 1} / {items.length}</p>
            )}
          </div>

          {items.length > 1 && (
            <button
              className="galleryLightboxNav galleryLightboxNav--next"
              onClick={(e) => { e.stopPropagation(); showNext(); }}
              aria-label="Next image"
            >
              &#8250;
            </button>
          )}
        </div>
      )}
    </>
  );
}
